import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageSquare, ChevronLeft, Crown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AutoTextarea } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useLiveSocket } from "@/lib/useLiveSocket";

export default function Messages() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [hostId, setHostId] = useState(null);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => api.get("/dm/threads").then(({ data }) => setThreads(data)).catch(() => {}), []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { api.get("/users/host-id").then(({ data }) => setHostId(data)).catch(() => {}); }, []);

  const loadMsgs = useCallback(() => { if (id) api.get(`/dm/${id}`).then(({ data }) => setMsgs(data)).catch(() => {}); }, [id]);
  useEffect(() => { loadMsgs(); }, [loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  // live DM updates
  useLiveSocket(useCallback((evt) => {
    if (evt.type !== "dm") return;
    const m = evt.message;
    const inThread = id && (m.from_id === id || m.to_id === id);
    if (inThread) {
      setMsgs((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      if (m.from_id === id) api.get(`/dm/${id}`).catch(() => {}); // mark read
    }
    loadThreads();
  }, [id, loadThreads]));

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post("/dm", { to_user_id: id, text: text.trim() }); setText(""); loadMsgs(); loadThreads(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const messageHost = () => { if (hostId && hostId.host_id !== user?.id) nav(`/messages/${hostId.host_id}`); };

  // Conversation view
  if (id) {
    const other = threads.find((t) => t.user_id === id);
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>
        <button onClick={() => nav("/messages")} className="flex items-center gap-1 text-slate-400 hover:text-gold text-sm mb-3"><ChevronLeft className="w-4 h-4" /> Inbox</button>
        <h2 className="font-display text-xl font-bold text-white mb-3">{other?.name || (hostId?.host_id === id ? hostId.host_name : "Conversation")}</h2>
        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pb-4" data-testid="dm-thread">
          {msgs.map((m) => {
            const mine = m.from_id === user?.id;
            return <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}><div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap break-words ${mine ? "gold-gradient text-obsidian" : "card-surface text-white"}`}>{m.text}</div></div>;
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex items-end gap-2 glass rounded-2xl p-2 border border-gold/20">
          <AutoTextarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 bg-transparent text-white placeholder-slate-500 focus:outline-none text-sm px-2" data-testid="dm-input" />
          <button type="submit" className="gold-gradient text-obsidian p-2.5 rounded-xl" data-testid="dm-send"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    );
  }

  // Inbox list
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-black text-white">Messages</h1>
        {hostId && hostId.host_id !== user?.id && <button onClick={messageHost} className="gold-gradient text-obsidian text-sm font-bold px-4 py-2 rounded-xl flex items-center gap-2" data-testid="message-host"><Crown className="w-4 h-4" /> Message the host</button>}
      </div>
      {threads.length === 0 ? (
        <div className="card-surface rounded-2xl p-16 text-center text-slate-400 mt-6"><MessageSquare className="w-10 h-10 mx-auto text-gold/40 mb-3" />No conversations yet.</div>
      ) : (
        <div className="mt-6 space-y-2" data-testid="dm-threads">
          {threads.map((t) => (
            <button key={t.user_id} onClick={() => nav(`/messages/${t.user_id}`)} className="w-full card-surface rounded-xl p-4 flex items-center gap-3 hover:border-gold/40 text-left" data-testid={`thread-${t.user_id}`}>
              <div className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center text-obsidian font-bold">{t.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-semibold text-white">{t.name}</span>{t.role === "admin" && <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded gold-gradient text-obsidian">HOST</span>}</div>
                <p className="text-slate-400 text-sm truncate">{t.last}</p>
              </div>
              {t.unread > 0 && <span className="bg-gold text-obsidian text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{t.unread}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
